import { Formik, Form, Field } from 'formik'
import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import { createSettings } from '../../core/_requests'
import { useAuth } from '../../../auth'
import { toast } from 'react-toastify'
import { useSettingsData } from '../../../../hooks/getSettingsData'
import { InfoCard2 } from '../../../widgets/components/UI/InfoCard2'
import Skeleton from 'react-loading-skeleton'

const NotificationsGeneral = () => {
  const { formatMessage } = useIntl()
  const { companyId } = useAuth()
  const [loading, setLoading] = useState<boolean>(false)
  const { notificationData, aiEmailLoading } = useSettingsData()

  const initialValues = {
    fileUploads: notificationData?.fileUploads || false,
    documentsModifications: notificationData?.documentModifications || false,
    fileDeletions: notificationData?.fileDeletion || false,
    retentionPeriodExpiry: notificationData?.retentionPeriodExpiry || false,
    archivalOfDocuments: notificationData?.archivalOfDocuments || false,
    legalHoldStatus: notificationData?.legalHoldStatus || false,
    sharedLinkCreationExpiration: notificationData?.sharedLink || false,
    accessRequests: notificationData?.accessRequests || false,
  }

  const notifications = [
    {
      id: 'File Uploads',
      tooltip:
        'Turn this on to receive a notification when a new file is uploaded under RecordsKeeper AI.',
      name: 'fileUploads',
    },
    {
      id: 'Document Modifications',
      tooltip:
        'Turn this on to receive a notification whenever a document is modified by any team member. ',
      name: 'documentsModifications',
    },
    {
      id: 'File Deletions',
      tooltip:
        'Turn this on to receive a notification whenever a file(s) has been deleted by a team member.',
      name: 'fileDeletions',
    },
    {
      id: 'Retention Period Expiry',
      tooltip:
        'Turn this on to receive a notification whenever the retention period for documents is expired. ',
      name: 'retentionPeriodExpiry',
    },
    {
      id: 'Archival of Documents',
      tooltip: 'Turn this on to receive a notification when a document is archived.',
      name: 'archivalOfDocuments',
    },
    {
      id: 'Legal Hold Status Changes',
      tooltip:
        'Turn this on to receive a notification when te Legal Hold Status changes for a document.',
      name: 'legalHoldStatus',
    },
    {
      id: 'Shared Link Creation/Expiration',
      tooltip:
        'Turn this on to receive a notification whenever a new Share Link is either created or has expired.',
      name: 'sharedLinkCreationExpiration',
    },
    {
      id: 'Access Requests',
      tooltip:
        'Turn this on to receive a notification when someone requests to access your documents.',
      name: 'accessRequests',
    },
  ]

  const handleSubmit = async (values: any, { resetForm }: any) => {
    if (!companyId) return
    setLoading(true)
    const payload = {
      fileUploads: values?.fileUploads,
      documentModifications: values?.documentsModifications,
      fileDeletion: values?.fileDeletions,
      retentionPeriodExpiry: values?.retentionPeriodExpiry,
      archivalOfDocuments: values?.archivalOfDocuments,
      sharedLink: values?.sharedLinkCreationExpiration,
      accessRequests: values?.accessRequests,
      legalHoldStatus: values?.legalHoldStatus,
    }
    try {
      const {
        data: { success, errors },
      } = await createSettings(companyId, payload)
      if (success) {
        toast.success('Settings saved successfully')
      } else {
        toast.error(errors.name)
      }
    } catch (err) {
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <InfoCard2
        title={'Manage Notification Preferences'}
        desc={`Use this page to customize how and when you receive notifications related to RecordsKeeper activities. Choose notification frequency (instant, daily, weekly, or monthly) and select the specific events—such as file uploads, modifications, deletions, and more—that you wish to be notified about.`}
        slug={
          'https://help.recordskeeper.ai/en/articles/10410431-how-to-manage-notification-preferences-for-your-recordskeeper-ai-account'
        }
      />
      <div className='bg-white rounded p-4 mb-8'>
        {aiEmailLoading ? (
          <>
            <Skeleton
              height={50}
              baseColor='var(--kt-skeleton-bg-color)'
              highlightColor='var(--kt-skeleton-highlight-bg-color)'
            />
            <Skeleton
              height={50}
              baseColor='var(--kt-skeleton-bg-color)'
              highlightColor='var(--kt-skeleton-highlight-bg-color)'
            />
            <Skeleton
              height={50}
              baseColor='var(--kt-skeleton-bg-color)'
              highlightColor='var(--kt-skeleton-highlight-bg-color)'
            />
            <Skeleton
              height={50}
              baseColor='var(--kt-skeleton-bg-color)'
              highlightColor='var(--kt-skeleton-highlight-bg-color)'
            />
            <Skeleton
              height={50}
              baseColor='var(--kt-skeleton-bg-color)'
              highlightColor='var(--kt-skeleton-highlight-bg-color)'
            />
            <Skeleton
              height={50}
              baseColor='var(--kt-skeleton-bg-color)'
              highlightColor='var(--kt-skeleton-highlight-bg-color)'
            />
            <Skeleton
              height={50}
              baseColor='var(--kt-skeleton-bg-color)'
              highlightColor='var(--kt-skeleton-highlight-bg-color)'
            />
            <Skeleton
              height={50}
              baseColor='var(--kt-skeleton-bg-color)'
              highlightColor='var(--kt-skeleton-highlight-bg-color)'
            />
            <Skeleton
              height={75}
              baseColor='var(--kt-skeleton-bg-color)'
              highlightColor='var(--kt-skeleton-highlight-bg-color)'
            />
          </>
        ) : (
          <Formik initialValues={initialValues} onSubmit={handleSubmit} enableReinitialize={true}>
            {(formik) => (
              <Form>
                <div className='row'>
                  <div>
                    {notifications.map(({ id, tooltip, name }) => (
                      <div
                        key={name}
                        className='form-check form-check-custom form-check-solid form-check-primary form-switch my-2 border-bottom pb-2'
                      >
                        <label className='togglebtn mx-0'>
                          <Field
                            className='togglebtn__input'
                            name={name}
                            type='checkbox'
                            formik={formik}
                          />
                          <div className='togglebtn__fill'></div>
                        </label>
                        <div className='d-flex flex-column justify-content-end mt-2'>
                          <div className='d-flex'>
                            <div>
                              <label
                                className='form-check-label fw-semibold font-size-13 title-notification'
                                data-bs-toggle='tooltip'
                              >
                                {formatMessage({ id })}
                              </label>
                            </div>
                          </div>
                          <label
                            className='form-check-label text-clr58 fw-normal fs-7'
                            data-bs-toggle='tooltip'
                          >
                            {formatMessage({ id: tooltip })}
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className='d-flex justify-content-end'>
                    <button
                      type='submit'
                      className='btn bg-primary text-white custom-button-property border border-1 mb-md-4 mb-0 px-6 py-4 text-center w-100 w-md-auto order-1 order-md-2'
                      disabled={loading}
                    >
                      {!loading && (
                        <span className='indicator-label font-size-13 p-0'>
                          {formatMessage({ id: 'Save Changes' })}
                        </span>
                      )}
                      {loading && (
                        <span className='indicator-label font-size-13 p-0'>
                          {formatMessage({ id: 'Please wait...' })}
                          <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                        </span>
                      )}
                    </button>
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        )}
      </div>
    </>
  )
}

export default NotificationsGeneral